import { Controller } from '../utils/controller';
import {
  formUtilHideError,
  formUtilIsErrorDisplayed,
  formUtilTogglePasswordVisibility,
  formUtilValidateEmail
} from '../utils/accountFormUtils';

interface IFormTarget {
  inputContainer: HTMLElement;
  input: HTMLInputElement;
  error: HTMLInputElement;
}

interface ISignInActiveInputTargets {
  email: IFormTarget;
  password: IFormTarget;
}

export default class extends Controller {
  readonly cssClass = {
    errorField: 'elc-floating-label-input-errorField',
    errorInputContainer: 'elc-floating-label-input-error'
  };

  static targets = [
    'signInEmailContainer',
    'signInEmailInput',
    'signInEmailError',
    'signInEyeIcon',
    'signInPasswordContainer',
    'signInPasswordInput',
    'signInPasswordError'
  ];

  declare signInEmailContainerTarget: HTMLElement;
  declare signInEmailInputTarget: HTMLInputElement;
  declare signInEmailErrorTarget: HTMLInputElement;
  declare signInEyeIconTarget: HTMLElement;
  declare signInPasswordContainerTarget: HTMLElement;
  declare signInPasswordInputTarget: HTMLInputElement;
  declare signInPasswordErrorTarget: HTMLInputElement;

  getSignInActiveInputTargets(): ISignInActiveInputTargets {
    return {
      email: {
        input: this.signInEmailInputTarget,
        error: this.signInEmailErrorTarget,
        inputContainer: this.signInEmailContainerTarget
      },
      password: {
        input: this.signInPasswordInputTarget,
        error: this.signInPasswordErrorTarget,
        inputContainer: this.signInPasswordContainerTarget
      }
    };
  }

  validateEmail(event: Event): void {
    const { email } = this.getSignInActiveInputTargets();

    formUtilValidateEmail(this, event, email);
  }

  togglePasswordVisibility() {
    formUtilTogglePasswordVisibility(this.signInEyeIconTarget, this.signInPasswordInputTarget);
  }

  validatePassword(event: Event): void {
    const { password } = this.getSignInActiveInputTargets();

    if (event.type === 'blur' || this.isErrorDisplayed(password.error)) {
      password.input.value ? this.hideError(password) : this.showError(password);
    }
  }

  isErrorDisplayed(target: HTMLElement): boolean {
    return formUtilIsErrorDisplayed(target);
  }

  showError(target: IFormTarget): void {
    const { error, input, inputContainer } = target;

    error.style.display = 'block';
    input.classList.add(this.cssClass.errorField);
    inputContainer.classList.add(this.cssClass.errorInputContainer);

    const {
      elc_account_errors: { email_invalid, email_required, password_required }
    } = window.site.translations;

    if (input.type === 'password') {
      error.textContent = password_required;
    } else {
      error.textContent = input.value ? email_invalid : email_required;
    }
  }

  hideError(target: IFormTarget): void {
    const { error, input, inputContainer } = target;
    formUtilHideError(this, error, input, inputContainer);
  }

  submitSignInForm(event: Event): void {
    const { email, password } = this.getSignInActiveInputTargets();

    const hasErrors = this.isErrorDisplayed(email.error) || this.isErrorDisplayed(password.error);
    const hasNoEmail = !email.input.value;
    const hasNoPassword = !password.input.value;

    if (hasNoEmail || hasNoPassword || hasErrors) {
      event.preventDefault();

      hasNoEmail && this.showError(email);
      hasNoPassword && this.showError(password);
    } else {
      const submitButton = event.target as HTMLButtonElement;
      submitButton.disabled = true;
      submitButton.textContent = submitButton.getAttribute('data-disable-text');

      const form = submitButton.parentElement as HTMLFormElement;
      form.submit();
    }
  }

  submitEmail(event: Event) {
    const { email } = this.getSignInActiveInputTargets();

    const hasErrors = this.isErrorDisplayed(email.error);
    const hasNoEmail = !email.input.value;

    if (hasNoEmail || hasErrors) {
      event.preventDefault();

      hasNoEmail && this.showError(email);
    } else {
      const submitButton = event.target as HTMLButtonElement;
      submitButton.disabled = true;
      submitButton.textContent = submitButton.getAttribute('data-disable-text');

      const goBackLink = document.getElementById('goBackLink') as HTMLAnchorElement;
      goBackLink.style.pointerEvents = 'none';

      const form = submitButton.parentElement as HTMLFormElement;
      form.submit();
    }
  }
}
